/**
 * Structural validation of lesson content (content-schema.md §4 step 3). Runs on lessons
 * loaded from the database before the player uses them, and in tests on the seed lessons.
 */
import type { Lesson } from './content.ts';
import { allSentences } from './content.ts';
import { buildPlan, hasSteps } from './lesson-plan.ts';

export interface ValidationIssue {
  path: string;
  message: string;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** Returns a list of problems; an empty list means the lesson can be played. */
export function validateLesson(raw: unknown): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const fail = (path: string, message: string) => issues.push({ path, message });

  if (!isObject(raw)) return [{ path: '', message: 'урок должен быть объектом' }];
  if (raw.schemaVersion !== '1.0') fail('schemaVersion', 'ожидается "1.0"');
  if (!isString(raw.copyrightNote)) fail('copyrightNote', 'обязательное поле');
  if (!['zh-CN', 'en-US', 'tr-TR'].includes(String(raw.targetLang))) fail('targetLang', 'неизвестный язык');
  if (!isObject(raw.title) || !isString(raw.title.text)) fail('title.text', 'обязательное поле');
  for (const key of ['texts', 'words', 'grammar', 'exercises', 'tasks'] as const) {
    if (!Array.isArray(raw[key])) fail(key, 'должен быть массивом');
  }
  if (!isObject(raw.appSteps)) fail('appSteps', 'обязательное поле');
  if (issues.length > 0) return issues;

  const lesson = raw as unknown as Lesson;
  const wordIds = new Set<string>();
  lesson.words.forEach((w, i) => {
    if (!isString(w.id) || !isString(w.word)) fail(`words[${i}]`, 'нужны id и word');
    if (wordIds.has(w.id)) fail(`words[${i}].id`, `повтор id ${w.id}`);
    wordIds.add(w.id);
  });

  const sentenceIds = new Set<string>();
  lesson.texts.forEach((t, ti) => {
    if (!Array.isArray(t.sentences)) {
      fail(`texts[${ti}].sentences`, 'должен быть массивом');
      return;
    }
    t.sentences.forEach((s, si) => {
      const path = `texts[${ti}].sentences[${si}]`;
      if (!isString(s.id) || !isString(s.text)) fail(path, 'нужны id и text');
      if (sentenceIds.has(s.id)) fail(`${path}.id`, `повтор id ${s.id}`);
      sentenceIds.add(s.id);
      if (!Array.isArray(s.tokens)) fail(`${path}.tokens`, 'должен быть массивом');
      (s.newWordIds ?? []).forEach((id) => {
        if (!wordIds.has(id)) fail(`${path}.newWordIds`, `нет слова ${id}`);
      });
      (s.tokens ?? []).forEach((tok, ki) => {
        if (tok.wordId && !wordIds.has(tok.wordId)) fail(`${path}.tokens[${ki}]`, `нет слова ${tok.wordId}`);
      });
      if (s.tokensStatus === 'done' && s.tokens.map((tok) => tok.surface).join('') !== s.text) {
        fail(`${path}.tokens`, 'токены не складываются в текст предложения');
      }
    });
  });

  lesson.words.forEach((w, i) => {
    if (w.exampleSentenceId && !sentenceIds.has(w.exampleSentenceId)) {
      fail(`words[${i}].exampleSentenceId`, `нет предложения ${w.exampleSentenceId}`);
    }
  });

  const grammarIds = new Set(lesson.grammar.map((g) => g.id));
  const planSteps = [
    ...(lesson.appSteps.days ?? []).flatMap((d) => d.steps),
    ...(lesson.appSteps.steps ?? []),
  ];
  for (const [stepId] of planSteps) {
    for (const part of stepId.split('+')) {
      const [name, arg] = part.split(':');
      if (name === 'grammar' && arg) {
        arg.split(',').forEach((id) => {
          if (!grammarIds.has(id)) fail('appSteps', `нет грамматики ${id}`);
        });
      }
      if ((name === 'exercises' || name === 'exercise') && arg) {
        arg.split(',').forEach((id) => {
          if (!lesson.exercises.some((e) => e.id === id)) fail('appSteps', `нет упражнения ${id}`);
        });
      }
    }
  }

  if (issues.length === 0 && allSentences(lesson).length > 0 && !hasSteps(buildPlan(lesson))) {
    fail('appSteps', 'в плане нет ни одного поддерживаемого шага');
  }
  return issues;
}
